import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import useSWR from "swr";
import { Landmark, Building2, Calculator, BarChart3, FolderOpen, ShieldCheck, LogOut, Menu, X, Scale } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { DisclaimerCtx } from "./DisclaimerContext";
import { IronDivider } from "@/components/common/Ornament";

const NAV = [
  { to: "/overview", label: "Overview", icon: Landmark, id: "overview" },
  { to: "/property", label: "The Property", icon: Building2, id: "property" },
  { to: "/financials", label: "Financial Model", icon: Calculator, id: "financials" },
  { to: "/comps", label: "Comparable Sales", icon: BarChart3, id: "comps" },
  { to: "/benchmarks", label: "Market Benchmarks", icon: Scale, id: "benchmarks" },
  { to: "/documents", label: "Documents", icon: FolderOpen, id: "documents" },
];

function NavItems({ role, onNav }) {
  const items = role === "admin" ? [...NAV, { to: "/admin", label: "Admin Dashboard", icon: ShieldCheck, id: "admin" }] : NAV;
  return (
    <nav className="space-y-1">
      {items.map((n) => (
        <NavLink key={n.to} to={n.to} onClick={onNav} data-testid={`nav-item-${n.id}`}
          className={({ isActive }) => `group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-[#C59B27]/15 text-[#D4AF37] font-semibold" : "text-[#F9F6F0]/75 hover:text-[#F9F6F0] hover:bg-white/5"}`}>
          <n.icon className="h-4 w-4 shrink-0" />
          {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

function Sidebar({ content, role, logout, onNav }) {
  return (
    <div className="flex h-full flex-col p-6">
      <p className="eyebrow !text-[#D4AF37]/80">New Orleans · LA</p>
      <h2 className="font-display text-2xl font-semibold text-[#F9F6F0] leading-tight mt-2" data-testid="sidebar-project-name">{content?.project_name || "—"}</h2>
      <IronDivider light className="my-6" />
      <NavItems role={role} onNav={onNav} />
      <div className="mt-auto pt-8">
        <div className="rounded-md border border-[#C59B27]/30 px-3 py-2 mb-3">
          <p className="text-[0.65rem] uppercase tracking-[0.2em] text-[#F9F6F0]/50">Signed in as</p>
          <p className="text-sm font-semibold text-[#D4AF37]" data-testid="sidebar-role-badge">{role === "admin" ? "Administrator" : "Investor / Partner"}</p>
        </div>
        <button onClick={logout} className="w-full flex items-center justify-center gap-2 h-10 rounded-md border border-white/15 text-sm text-[#F9F6F0]/85 hover:bg-white/5 hover:text-white transition-colors" data-testid="nav-logout-btn">
          <LogOut className="h-4 w-4" /> Log out
        </button>
      </div>
    </div>
  );
}

export default function AppShell() {
  const { role, logout } = useAuth();
  const { data: content } = useSWR("/content");
  const [discOpen, setDiscOpen] = useState(false);
  const [mobile, setMobile] = useState(false);
  const loc = useLocation();

  return (
    <DisclaimerCtx.Provider value={{ open: () => setDiscOpen(true) }}>
      <div className="min-h-screen flex">
        <aside className="hidden lg:block w-72 shrink-0 iron-pattern fixed inset-y-0 left-0 z-30" data-testid="sidebar">
          <Sidebar content={content} role={role} logout={logout} />
        </aside>
        {mobile && (
          <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-nav">
            <div className="absolute inset-0 bg-black/50" onClick={() => setMobile(false)} />
            <aside className="absolute inset-y-0 left-0 w-72 iron-pattern">
              <button onClick={() => setMobile(false)} className="absolute right-3 top-3 text-white/70" data-testid="mobile-nav-close"><X className="h-5 w-5" /></button>
              <Sidebar content={content} role={role} logout={logout} onNav={() => setMobile(false)} />
            </aside>
          </div>
        )}
        <div className="flex-1 lg:ml-72 min-w-0 flex flex-col">
          <header className="lg:hidden sticky top-0 z-40 iron-pattern flex items-center justify-between px-4 h-14">
            <button onClick={() => setMobile(true)} className="text-[#F9F6F0]" data-testid="mobile-nav-open"><Menu className="h-5 w-5" /></button>
            <span className="font-display text-lg text-[#F9F6F0] truncate px-3">{content?.project_name}</span>
            <button onClick={logout} className="text-[#D4AF37]" data-testid="header-logout-btn" aria-label="Log out"><LogOut className="h-5 w-5" /></button>
          </header>
          <main key={loc.pathname} className="flex-1 p-4 sm:p-8 lg:p-12 max-w-[1500px] w-full mx-auto">
            <Outlet />
          </main>
          <footer className="sticky bottom-0 z-20 border-t border-[#E2DCD2] bg-white/85 backdrop-blur-md px-4 sm:px-8 lg:px-12 py-4 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between text-xs text-slate-600" data-testid="persistent-footer">
            <p data-testid="footer-disclaimer-text">
              Projections are hypothetical and not guaranteed.{" "}
              <button onClick={() => setDiscOpen(true)} className="underline font-semibold text-[#0F2E23]" data-testid="footer-disclaimer-link">See full disclaimer.</button>
            </p>
            <div className="flex items-center gap-4">
              <span className="text-slate-400">Confidential – NOLA Project</span>
              <button onClick={logout} className="inline-flex items-center gap-1 font-semibold text-[#0F2E23] hover:text-[#C59B27]" data-testid="footer-logout-btn"><LogOut className="h-3.5 w-3.5" /> Log out</button>
            </div>
          </footer>
        </div>
      </div>
      <Dialog open={discOpen} onOpenChange={setDiscOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-[#F9F6F0]" data-testid="disclaimer-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-[#0F2E23]">Full Disclaimer</DialogTitle>
            <DialogDescription>{content?.project_name}</DialogDescription>
          </DialogHeader>
          <p className="text-sm leading-relaxed text-slate-800 whitespace-pre-line" data-testid="disclaimer-modal-text">{content?.disclaimer}</p>
        </DialogContent>
      </Dialog>
    </DisclaimerCtx.Provider>
  );
}
